import {Alert, AlertTitle, Box, Button, Tooltip, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useContext, useState} from 'react';
import {ScreenPaths} from '../../../../../enums/screen-path';
import {translations} from '../../../../../i18n/translations';
import {SnackBarContextType} from '../../../../../contexts/snack-bar/types';
import {SnackBarContext} from '../../../../../contexts/snack-bar/context';
import {NotificationsRepository} from '../../../../../repositories/notifications';
import {AuthContext} from '../../../../../contexts/auth/context';
import {AuthContextType} from '../../../../../contexts/auth/types';
import {useInfiniteLoading} from '../../../../../hooks/infinite-loading';
import {CancelToken} from 'axios';
import {PushNotificationsState} from '../../../../../enums/push-notifications-state';
import GenericHeader from '../../../../../components/generic-header/generic-header';
import {Refresh} from '@mui/icons-material';
import './notifications.me.screen.scss';
import {SectionList} from '../../../../../components/section-list/section-list';
import {useEntity} from '../../../../../contexts/entity/context';
import {
  NotificationCard,
  NotificationPendingCard,
} from '../../../../../components/notification-card/notification-card';
import {NotificationsAllReadModal} from '../../../../../components/notification-modals/notifications-all-read-modal';

export default function NotificationsMeOfficeScreen() {
  const {t} = useTranslation();
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const authContext: AuthContextType = useContext(AuthContext);
  const {
    updateEntityPendingTasks,
    state: {pendingTasks},
  } = useEntity();
  const [readAllDialogOpened, setReadAllDialogOpened] = useState<boolean>(false);
  const [submittingReadAll, setSubmittingReadAll] = useState<boolean>(false);

  // Fetch notifications
  const handleFetchData = async (
    limit: number,
    skip: number,
    cancelToken?: CancelToken
  ): Promise<{
    totalCount: number;
    data: Array<{
      id: string;
      item: any;
      date?: string;
    }>;
  }> => {
    // Pending tasks are supplementary to notifications and must not block this request.
    void updateEntityPendingTasks().catch((error) => {
      console.debug('🔴 Could not refresh pending tasks.', error);
    });

    try {
      const results = await NotificationsRepository.instance.get(limit, skip, cancelToken);

      return {
        totalCount: results.totalCount,
        data: results.data.map((notification) => {
          return {
            id: notification.id || '',
            date: notification.createdAt ? (notification.createdAt as any) : undefined,
            item: notification,
          };
        }),
      };
    } catch (error) {
      console.debug('🔴 Could not fetch notifications.');

      return {
        totalCount: 0,
        data: [],
      };
    }
  };

  // Infinite loading hook
  const {
    sectionListData,
    isLoading,
    allLoaded,
    handleRefresh,
    updateAllSectionListItemsForProperty,
  } = useInfiniteLoading({
    handleFetchData,
    type: 'section-new-old-f',
  });

  const handleReadAllNotifications = async () => {
    try {
      setSubmittingReadAll(true);

      // Read all notifications
      await NotificationsRepository.instance.updateStatusAll(true);

      // Update notification count in context
      authContext.updateSessionUnreadNotificationCount(0);

      // Update "wasRead" property on all items
      updateAllSectionListItemsForProperty('wasRead', true);

      setReadAllDialogOpened(false);
      setSubmittingReadAll(false);

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.notifications.read_all_notifications_success_message),
      });
    } catch (error) {
      // Error
      console.debug('🔴 Could not read all notifications.', error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.notifications.read_all_notifications_error_message),
      });
      setSubmittingReadAll(false);
    }
  };

  // Submit button
  const renderSubmitButton = (disabled: boolean) => (
    <Box className="notification-read-all-button">
      <Button
        disabled={disabled}
        color="primary"
        variant="text"
        onClick={() => setReadAllDialogOpened(true)}
      >
        {t(translations.notifications.read_all_notifications)}
      </Button>
    </Box>
  );

  return (
    <Box>
      <GenericHeader
        titleCrumbs={[
          {
            label: t(translations.notifications.title),
            link: `/office${ScreenPaths.Office_Me}${ScreenPaths.Me_Notifications}`,
          },
        ]}
        isLoading={false}
        leftColumn={<></>}
        rightColumn={
          <Button
            className="refresh-button"
            variant="inverted"
            color="primary"
            startIcon={<Refresh />}
            onClick={() => {
              handleRefresh();
              updateEntityPendingTasks();
            }}
          >
            {t(translations.section_list.refresh)}
          </Button>
        }
      />
      <Box className="notifications-container">
        <Box className="notifications-container-centered">
          {authContext.state.session?.unreadNotificationsCount &&
          authContext.state.session.unreadNotificationsCount !== 0 ? (
            renderSubmitButton(false)
          ) : (
            <Tooltip
              title={
                t(
                  translations.notifications.read_all_notifications_disabled_tooltip_message
                ) as string
              }
              placement="bottom-end"
            >
              {renderSubmitButton(true)}
            </Tooltip>
          )}
          {(authContext.state.pushNotificationsState ===
            PushNotificationsState.UnsubscribedNotSupported ||
            authContext.state.pushNotificationsState === PushNotificationsState.UnsubscribedError ||
            authContext.state.pushNotificationsState ===
              PushNotificationsState.UnsubscribedPermissionsNotGranted) && (
            <Alert severity="warning" sx={{marginBottom: '24px'}}>
              <AlertTitle>
                {t(translations.push_notifications.warnings.unsubscribed_generic_warning_title)}
              </AlertTitle>
              {authContext.state.pushNotificationsState ===
                PushNotificationsState.UnsubscribedNotSupported && (
                <Typography variant="body2">
                  {t(
                    translations.push_notifications.warnings
                      .unsubscribed_not_supported_warning_message
                  )}
                </Typography>
              )}
              {authContext.state.pushNotificationsState ===
                PushNotificationsState.UnsubscribedError && (
                <Typography variant="body2">
                  {t(translations.push_notifications.warnings.unsubscribed_error_warning_message)}
                </Typography>
              )}
              {authContext.state.pushNotificationsState ===
                PushNotificationsState.UnsubscribedPermissionsNotGranted && (
                <Typography variant="body2">
                  {t(
                    translations.push_notifications.warnings
                      .unsubscribed_permissions_denied_warning_message
                  )}
                </Typography>
              )}
            </Alert>
          )}
          {pendingTasks.length > 0 && <NotificationPendingCard data={pendingTasks} />}
          <SectionList
            data={sectionListData}
            isLoading={isLoading}
            allLoaded={allLoaded}
            renderItem={(item) => (
              <NotificationCard key={`notification-card-${item.id}`} notification={item} />
            )}
          />
        </Box>
      </Box>
      {readAllDialogOpened && (
        <NotificationsAllReadModal
          submitting={submittingReadAll}
          open
          onRead={handleReadAllNotifications}
          onClose={() => setReadAllDialogOpened(false)}
        />
      )}
    </Box>
  );
}
